
import React, { useState, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { EnhancedRoomMatch, HydratedMatchGroup } from "../types";
import { parseLineItemNum } from "../utils";
import { ComparisonFilters } from "@/hooks/useComparisonState";
import { CollapsibleLineItemRoomCard } from './CollapsibleLineItemRoomCard';
import { Button } from "@/components/ui/button";
import { ChevronsDown, ChevronsUp } from "lucide-react";
import { BulkMatchActionBar } from './BulkMatchActionBar';

interface LineItemMatchesTabProps {
    groups: HydratedMatchGroup[];
    roomMatches: EnhancedRoomMatch[];
    filters: ComparisonFilters;
    onApprove: (groupId: number) => void;
    onReject: (groupId: number) => void;
    onRematchRoom: (roomId: number) => void;
    onMoveLineItem?: (itemId: number, targetLineItemMatchGroupId: number | null) => void;
    onCreateGroup?: (insurerLineItemIds: number[], contractorLineItemIds: number[], roomMatchGroupId: number | null) => void;
    onUpdateLineItemMatchGroup?: (itemId: number, lineItemMatchGroupId: number | null) => void;
    selectedItemIds?: Set<number>;
    onToggleItemSelection?: (itemId: number) => void;
    onClearSelection?: () => void;
}

export const LineItemMatchesTab: React.FC<LineItemMatchesTabProps> = ({
    groups,
    roomMatches,
    filters,
    onApprove,
    onReject,
    onRematchRoom,


    onMoveLineItem,
    onCreateGroup,
    onUpdateLineItemMatchGroup,
    selectedItemIds,
    onToggleItemSelection,
    onClearSelection
}) => {

    const [collapsedRoomIds, setCollapsedRoomIds] = useState<Set<number>>(new Set());

    // Sort room matches by the minimum insurer lineItemNumber across all items in the room
    const sortedRoomMatches = [...roomMatches].sort((a, b) => {
        const allInsurerA = a.insurerDetails?.flatMap(d => d.items) || [];
        const allInsurerB = b.insurerDetails?.flatMap(d => d.items) || [];
        const minA = allInsurerA.length > 0
            ? Math.min(...allInsurerA.map(i => parseLineItemNum(i.lineItemNumber)))
            : Infinity;
        const minB = allInsurerB.length > 0
            ? Math.min(...allInsurerB.map(i => parseLineItemNum(i.lineItemNumber)))
            : Infinity;
        if (minA !== minB) return minA - minB;
        // Fallback to alphabetical room name
        const nameA = a.roomA?.[0] || a.roomB?.[0] || '';
        const nameB = b.roomA?.[0] || b.roomB?.[0] || '';
        return nameA.localeCompare(nameB);
    });

    // Filter matches based on search query AND search terms (OR logic)
    const activeTerms = [filters.searchQuery, ...filters.searchTerms].filter(t => t.trim() !== '').map(t => t.toLowerCase());

    const filterFn = (text: string) => {
        if (activeTerms.length === 0) return true;
        return activeTerms.some(term => text.toLowerCase().includes(term));
    };

    // Check filters for Room Visibility
    const roomMatchResults = sortedRoomMatches.map(match => {
        // 1. Show Matched Filter (Review Status)
        // If showMatched is FALSE, HIDE Approved matches.
        if (!filters.showMatched && match.review?.status === 'approved') {
            return { match, visible: false, isRoomMatch: false };
        }

        // 2. Confidence Filter
        const conf = match.confidence || 0;
        if (filters.confidenceLevel === 'high' && conf < 0.8) return { match, visible: false, isRoomMatch: false };
        if (filters.confidenceLevel === 'medium' && (conf < 0.5 || conf >= 0.8)) return { match, visible: false, isRoomMatch: false };
        if (filters.confidenceLevel === 'low' && conf >= 0.5) return { match, visible: false, isRoomMatch: false };

        // 3. Search Filter AND Hide Empty Rooms Filter

        const roomAName = match.roomA?.join(', ') || '';
        const roomBName = match.roomB?.join(', ') || '';
        const matchText = `${roomAName} ${roomBName}`;

        // Basic Room Name Match
        let isMatch = filterFn(matchText);
        const roomMatch = isMatch;

        // If not matched by room name, check items
        if (!isMatch) {
            const allItems = [
                ...(match.insurerDetails?.flatMap(d => d.items) || []),
                ...(match.contractorDetails?.flatMap(d => d.items) || [])
            ];
            isMatch = allItems.some(item => filterFn(item.description || ''));
        }

        // Hide Empty Rooms Filter (if room has no items)
        if (filters.hideEmptyRooms) {
            const hasItems =
                (match.insurerDetails?.flatMap(d => d.items).length || 0) > 0 ||
                (match.contractorDetails?.flatMap(d => d.items).length || 0) > 0;
            if (!hasItems) return { match, visible: false, isRoomMatch: false };
        }

        return { match, visible: isMatch, isRoomMatch: roomMatch };
    });

    const visibleMatches = roomMatchResults.filter(d => d.visible);

    const allCollapsed = visibleMatches.every(m => collapsedRoomIds.has(m.match.id));

    // Calculate selected item counts by side
    const allInsurerItems = useMemo(() => 
        sortedRoomMatches.flatMap(match => 
            match.insurerDetails?.flatMap(d => d.items) || []
        ),
        [sortedRoomMatches]
    );

    const allContractorItems = useMemo(() => 
        sortedRoomMatches.flatMap(match => 
            match.contractorDetails?.flatMap(d => d.items) || []
        ),
        [sortedRoomMatches]
    );

    // Memoize sets of all insurer and contractor item IDs for efficient lookups
    const allInsurerItemIds = useMemo(() => {
        const ids = new Set<number>();
        sortedRoomMatches.forEach(match => {
            match.insurerDetails?.forEach(detail => {
                detail.items?.forEach(item => {
                    if (item.lineItemId) ids.add(item.lineItemId);
                });
            });
        });
        return ids;
    }, [sortedRoomMatches]);

    const allContractorItemIds = useMemo(() => {
        const ids = new Set<number>();
        sortedRoomMatches.forEach(match => {
            match.contractorDetails?.forEach(detail => {
                detail.items?.forEach(item => {
                    if (item.lineItemId) ids.add(item.lineItemId);
                });
            });
        });
        return ids;
    }, [sortedRoomMatches]);

    const selectedInsurerCount = useMemo(() => 
        Array.from(selectedItemIds || []).filter(id =>
            allInsurerItems.some(i => i.lineItemId === id)
        ).length,
        [selectedItemIds, allInsurerItems]
    );

    const selectedContractorCount = useMemo(() => 
        Array.from(selectedItemIds || []).filter(id =>
            allContractorItems.some(i => i.lineItemId === id)
        ).length,
        [selectedItemIds, allContractorItems]
    );

    const hasSelection = (selectedItemIds?.size || 0) > 0;

    // Determine which room the selected items belong to (for preventing cross-room matching)
    const selectedItemsRoomId = useMemo(() => {
        if (!selectedItemIds || selectedItemIds.size === 0) return null;
        
        // Find the room that contains any of the selected items
        for (const match of sortedRoomMatches) {
            const allItems = [
                ...(match.insurerDetails?.flatMap(d => d.items) || []),
                ...(match.contractorDetails?.flatMap(d => d.items) || [])
            ];
            
            const hasSelectedItem = allItems.some(item => 
                selectedItemIds.has(item.lineItemId!)
            );
            
            if (hasSelectedItem) {
                return match.id;
            }
        }
        
        return null;
    }, [selectedItemIds, sortedRoomMatches]);

    const handleBulkCreateMatch = () => {
        if (!selectedItemIds || !onCreateGroup) return;

        const selectedArray = Array.from(selectedItemIds);
        const insurerIds = selectedArray.filter(id =>
            allInsurerItems.some(i => i.lineItemId === id)
        );
        const contractorIds = selectedArray.filter(id =>
            allContractorItems.some(i => i.lineItemId === id)
        );

        if (insurerIds.length > 0 && contractorIds.length > 0) {
            // For bulk matching across rooms, we'll use null for roomMatchGroupId
            // The backend will need to handle this appropriately
            // Alternatively, we could find a common room or create separate groups per room
            onCreateGroup(insurerIds, contractorIds, null);
            onClearSelection?.();
        }
    };

    const toggleCollapseAll = () => {
        setCollapsedRoomIds(prev => {
            const next = new Set(prev);
            if (allCollapsed) {
                // Expand All -> Remove visible from collapsed set
                visibleMatches.forEach(m => next.delete(m.match.id));
            } else {
                // Collapse All -> Add visible to collapsed set
                visibleMatches.forEach(m => next.add(m.match.id));
            }
            return next;
        });
    };

    const handleToggleRoom = (roomId: number, open: boolean) => {
        setCollapsedRoomIds(prev => {
            const next = new Set(prev);
            if (open) next.delete(roomId);
            else next.add(roomId);
            return next;
        });
    };


    // Handle bulk action to add selected items to an existing group
    const handleAddSelectedToGroup = (groupId: number, selectedIds: number[]) => {
        if (selectedIds.length === 0 || !onMoveLineItem) return;

        // Find the target group to determine which side we're adding to
        const targetGroup = groups.find(g => g.id === groupId);
        if (!targetGroup) {
            console.error('Target group not found:', groupId);
            return;
        }

        // Determine which side of the group has items (insurer or contractor)
        const hasInsurerItems = targetGroup.insurerItems && targetGroup.insurerItems.length > 0;
        const hasContractorItems = targetGroup.contractorItems && targetGroup.contractorItems.length > 0;

        // Filter selected items to only include those from the same side as the target group
        const validItemIds = selectedIds.filter(itemId => {
            const isInsurerItem = allInsurerItemIds.has(itemId);
            const isContractorItem = allContractorItemIds.has(itemId);

            if (isInsurerItem && hasInsurerItems) return true;
            if (isContractorItem && hasContractorItems) return true;

            return false;
        });

        if (validItemIds.length === 0) {
            console.warn('No valid items to add to group (wrong side selected)');
            return;
        }

        // Move the valid items to the target group
        validItemIds.forEach(itemId => {
            onMoveLineItem(itemId, groupId);
        });

        // Clear selection after successful add
        if (onClearSelection) {
            onClearSelection();
        }
    };

    const handleDragEnd = (event: DragEndEvent, roomId: number) => {
        const { active, over } = event;
        if (!over) return;

        const itemId = Number(active.id);
        const targetId = over.id; // string or number
        const activeData = active.data.current as any;
        const overData = over.data.current as any;

        if (!onMoveLineItem) {
            console.warn("No move handler provided for LineItemMatchesTab");
            return;
        }

        // Case: Dragging to "Unmatched Zone"
        // Check data type first as it's more robust than ID string matching
        const isUnmatchedZone =
            (overData && overData.type === 'unmatched-zone') ||
            targetId === `unmatched-room-${roomId}`;

        if (isUnmatchedZone) {
            if (onUpdateLineItemMatchGroup) {
                onUpdateLineItemMatchGroup(itemId, null);
            } else {
                onMoveLineItem(itemId, null); // Keep fallback if needed, or remove
            }
            return;
        }

        // Case: Dragging Item onto another Unmatched Item (Create Group)
        if (overData && overData.type === 'unmatched-item') {
            const sourceItem = activeData;
            const targetItem = overData; // This has { type: 'unmatched-item', itemId, side } form

            // Determine source side by checking which set contains the item
            let sourceSide: 'insurer' | 'contractor' | undefined;

            // Try to find item in groups first (if it's already matched)
            if (sourceItem?.lineItemMatchGroupId) {
                const group = groups.find(g => g.id === sourceItem.lineItemMatchGroupId);
                if (group) {
                    if (group.insurerItems.some(i => i.lineItemId === itemId)) {
                        sourceSide = 'insurer';
                    } else if (group.contractorItems.some(i => i.lineItemId === itemId)) {
                        sourceSide = 'contractor';
                    }
                }
            }

            // Check all item IDs to determine side
            if (!sourceSide) {
                if (allInsurerItemIds.has(itemId)) {
                    sourceSide = 'insurer';
                } else if (allContractorItemIds.has(itemId)) {
                    sourceSide = 'contractor';
                }
            }

            const targetSide = targetItem.side;
            const targetItemId = targetItem.itemId;

            if (sourceSide && targetSide && sourceSide !== targetSide) {
                // Cross-side drop! Create Match Group.
                const insurerId = sourceSide === 'insurer' ? itemId : targetItemId;
                const contractorId = sourceSide === 'contractor' ? itemId : targetItemId;

                if (onCreateGroup) {
                    onCreateGroup([insurerId], [contractorId], roomId);
                }
                return;
            } else if (sourceSide && targetSide && sourceSide === targetSide) {
                // Dragging onto same-side unmatched item -> Unmatch (move to null group)

                if (onUpdateLineItemMatchGroup) {
                    onUpdateLineItemMatchGroup(itemId, null);
                } else {
                    onMoveLineItem(itemId, null);
                }
                return;
            } else {
                console.warn('Drag operation failed: Could not determine source or target side', {
                    sourceSide,
                    targetSide,
                    draggedItemId: itemId,
                    targetItemId,
                    roomId,
                    sourceItemData: sourceItem,
                    targetItemData: targetItem,
                    availableGroups: groups.length,
                    message: 'This may indicate missing drag data or an unmatched item without proper side information'
                });
            }
        }

        // Case: Dragging to a Match Group
        if (typeof targetId === 'string' && targetId.startsWith('match-group-')) {
            const groupId = Number(targetId.replace('match-group-', ''));

            // Check if valid group
            if (!isNaN(groupId)) {
                onMoveLineItem(itemId, groupId);
            }
        }
    };

    return (
        <div className="space-y-6 max-w-5xl mx-auto pb-20">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">
                    Line Item Matching ({visibleMatches.length} Rooms)
                </h3>
                <Button variant="outline" size="sm" onClick={toggleCollapseAll} className="gap-2">
                    {allCollapsed ? (
                        <>
                            <ChevronsDown className="h-4 w-4" />
                            Expand All
                        </>
                    ) : (
                        <>
                            <ChevronsUp className="h-4 w-4" />
                            Collapse All
                        </>
                    )}
                </Button>
            </div>

            <div className="grid gap-4">
                {visibleMatches.map(({ match, isRoomMatch }) => (
                    <CollapsibleLineItemRoomCard
                        key={match.id}
                        match={match}
                        allGroups={groups}
                        onApproveGroup={onApprove}
                        onRejectGroup={onReject}
                        onRematchRoom={onRematchRoom}
                        onDragEnd={handleDragEnd}
                        isOpen={!collapsedRoomIds.has(match.id)}
                        onOpenChange={(open) => handleToggleRoom(match.id, open)}
                        filters={filters}
                        isRoomMatch={isRoomMatch}
                        selectedItemIds={selectedItemIds}
                        onToggleItemSelection={onToggleItemSelection}
                        onAddSelectedToGroup={handleAddSelectedToGroup}
                        selectedItemsRoomId={selectedItemsRoomId}
                    />
                ))}

                {visibleMatches.length === 0 && (
                    <div className="text-center py-16 text-muted-foreground bg-muted/30 rounded-xl border border-dashed">
                        No rooms match your filters.
                    </div>
                )}
            </div>

            {/* Bulk Match Action Bar */}
            {hasSelection && (
                <BulkMatchActionBar
                    selectedInsurerCount={selectedInsurerCount}
                    selectedContractorCount={selectedContractorCount}
                    onCreateMatch={handleBulkCreateMatch}
                    onClearSelection={() => onClearSelection?.()}
                />
            )}
        </div>
    );
};
